import { useState } from "react";
import { Search } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";

// Mock data for clients and suppliers
const mockClientsAndSuppliers = [
  { id: "1", name: "João Silva", email: "joao@construtoraabc.com", type: "cliente", phone: "(11) 99999-1111" },
  { id: "2", name: "Maria Santos", email: "maria@grupoxyz.com", type: "cliente", phone: "(21) 88888-2222" },
  { id: "3", name: "Carlos Oliveira", email: "carlos@incorporadoradef.com", type: "cliente", phone: "(61) 77777-3333" },
  { id: "4", name: "Ana Costa", email: "ana@empresaghi.com", type: "cliente", phone: "(31) 66666-4444" },
  { id: "5", name: "Pedro Lima", email: "pedro@industriajkl.com", type: "cliente", phone: "(51) 55555-5555" },
  { id: "6", name: "Lucia Ferreira", email: "lucia@construtoramno.com", type: "cliente", phone: "(41) 44444-6666" },
  { id: "7", name: "Ricardo Materiais Ltda", email: "contato@ricardomateriais.com", type: "fornecedor", phone: "(11) 33333-7777" },
  { id: "8", name: "Equipamentos São Paulo", email: "vendas@equipsp.com", type: "fornecedor", phone: "(11) 22222-8888" },
  { id: "9", name: "Fornecedor ABC", email: "abc@fornecedor.com", type: "fornecedor", phone: "(21) 11111-9999" },
  { id: "10", name: "Materiais & Cia", email: "info@materiaisecia.com", type: "fornecedor", phone: "(31) 99999-0000" }
];

interface NovoUsuarioModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserCreated?: (userData: { name: string; email: string; role: string; phone: string }) => void;
}

export const NovoUsuarioModal = ({ open, onOpenChange, onUserCreated }: NovoUsuarioModalProps) => {
  const [isUserSearchOpen, setIsUserSearchOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  
  // Novo usuário form states
  const [userName, setUserName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userRole, setUserRole] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [userProfile, setUserProfile] = useState("");
  const [userStatus, setUserStatus] = useState("ativo");
  const [approveReport, setApproveReport] = useState(false);
  const [viewReportLevel, setViewReportLevel] = useState("");
  const [notifications, setNotifications] = useState({
    app: false,
    email: false,
    whatsapp: false
  });
  const [permissions, setPermissions] = useState({
    usuariosSistema: { add: false, edit: false, view: false },
    obra: { add: false, edit: false, view: false },
    relatorios: { add: false, edit: false, view: false },
    gestorTarefas: { add: false, edit: false, view: false },
    relatoriosGerenciais: { add: false, edit: false, view: false },
    obraIA: { add: false, edit: false, view: false },
    clientes: { add: false, edit: false, view: false },
    fornecedores: { add: false, edit: false, view: false },
    materiais: { add: false, edit: false, view: false },
    equipamentos: { add: false, edit: false, view: false },
    setores: { add: false, edit: false, view: false },
    horarioTrabalho: { add: false, edit: false, view: false },
    condicaoClimatica: { add: false, edit: false, view: false },
    maoObra: { add: false, edit: false, view: false },
    atividade: { add: false, edit: false, view: false },
    ocorrencia: { add: false, edit: false, view: false },
    checklist: { add: false, edit: false, view: false },
    controleMaterial: { add: false, edit: false, view: false },
    comentario: { add: false, edit: false, view: false },
    foto: { add: false, edit: false, view: false },
    video: { add: false, edit: false, view: false },
    anexo: { add: false, edit: false, view: false }
  });

  // Filter clients and suppliers based on search term
  const filteredClientsAndSuppliers = mockClientsAndSuppliers.filter(item =>
    item.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(userSearchTerm.toLowerCase())
  );

  // Function to select a user from the search results
  const selectUser = (user: typeof mockClientsAndSuppliers[0]) => {
    setUserName(user.name);
    setUserEmail(user.email);
    setUserPhone(user.phone);
    setIsUserSearchOpen(false);
    setUserSearchTerm("");
  };

  // Reset form when modal closes
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset all form fields
      setUserName("");
      setUserEmail("");
      setUserRole("");
      setUserPhone("");
      setUserPassword("");
      setUserProfile("");
      setUserStatus("ativo");
      setApproveReport(false);
      setViewReportLevel("");
      setNotifications({
        app: false,
        email: false,
        whatsapp: false
      });
      setPermissions({
        usuariosSistema: { add: false, edit: false, view: false },
        obra: { add: false, edit: false, view: false },
        relatorios: { add: false, edit: false, view: false },
        gestorTarefas: { add: false, edit: false, view: false },
        relatoriosGerenciais: { add: false, edit: false, view: false },
        obraIA: { add: false, edit: false, view: false },
        clientes: { add: false, edit: false, view: false },
        fornecedores: { add: false, edit: false, view: false },
        materiais: { add: false, edit: false, view: false },
        equipamentos: { add: false, edit: false, view: false },
        setores: { add: false, edit: false, view: false },
        horarioTrabalho: { add: false, edit: false, view: false },
        condicaoClimatica: { add: false, edit: false, view: false },
        maoObra: { add: false, edit: false, view: false },
        atividade: { add: false, edit: false, view: false },
        ocorrencia: { add: false, edit: false, view: false },
        checklist: { add: false, edit: false, view: false },
        controleMaterial: { add: false, edit: false, view: false },
        comentario: { add: false, edit: false, view: false },
        foto: { add: false, edit: false, view: false },
        video: { add: false, edit: false, view: false },
        anexo: { add: false, edit: false, view: false }
      });
    }
    onOpenChange(newOpen);
  };

  return (
    <>
      {/* Novo Usuário Dialog */}
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Novo Usuário</DialogTitle>
          </DialogHeader>
          <div className="grid gap-6 py-4">
            {/* Informações Básicas */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="userName">Nome do usuário <span className="text-red-500">*</span></Label>
                <div className="flex gap-2">
                  <Input
                    id="userName"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    placeholder="Digite o nome completo"
                  />
                  <Button 
                    type="button"
                    variant="outline" 
                    size="icon"
                    onClick={() => setIsUserSearchOpen(true)}
                  >
                    <Search className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userEmail">E-mail <span className="text-red-500">*</span></Label>
                <Input
                  id="userEmail"
                  type="email"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  placeholder="Digite o e-mail"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="userRole">Cargo</Label>
                <Input
                  id="userRole"
                  value={userRole}
                  onChange={(e) => setUserRole(e.target.value)}
                  placeholder="Digite o cargo"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="userPhone">Telefone</Label>
                <Input
                  id="userPhone"
                  value={userPhone}
                  onChange={(e) => setUserPhone(e.target.value)}
                  placeholder="Digite o telefone"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Senha</Label>
                <div className="flex gap-2">
                  <Input
                    type="password"
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    placeholder="Digite a senha"
                  />
                  <Button variant="outline" type="button">
                    Gerar Link
                  </Button>
                </div>
              </div>
              <div className="grid gap-2">
                <Label>Perfil</Label>
                <Select value={userProfile} onValueChange={(value) => {
                  setUserProfile(value);
                  
                  // Pre-configure permissions for "Cliente" profile
                  if (value === "cliente") {
                    setPermissions({
                      usuariosSistema: { add: false, edit: false, view: true },
                      obra: { add: false, edit: false, view: true },
                      relatorios: { add: false, edit: false, view: true },
                      gestorTarefas: { add: false, edit: false, view: false },
                      relatoriosGerenciais: { add: false, edit: false, view: false },
                      obraIA: { add: false, edit: false, view: false },
                      clientes: { add: false, edit: false, view: false },
                      fornecedores: { add: false, edit: false, view: true },
                      materiais: { add: false, edit: false, view: true },
                      equipamentos: { add: false, edit: false, view: false },
                      setores: { add: false, edit: false, view: false },
                      horarioTrabalho: { add: false, edit: false, view: true },
                      condicaoClimatica: { add: false, edit: false, view: true },
                      maoObra: { add: false, edit: false, view: true },
                      atividade: { add: false, edit: false, view: true },
                      ocorrencia: { add: false, edit: false, view: false },
                      checklist: { add: false, edit: false, view: false },
                      controleMaterial: { add: false, edit: false, view: false },
                      comentario: { add: false, edit: false, view: true },
                      foto: { add: false, edit: false, view: true },
                      video: { add: false, edit: false, view: true },
                      anexo: { add: false, edit: false, view: false }
                    });
                  } else if (value === "administrador") {
                    // Set all permissions for administrator
                    setPermissions(Object.keys(permissions).reduce((acc, key) => ({
                      ...acc,
                      [key]: { add: true, edit: true, view: true }
                    }), {} as typeof permissions));
                  } else if (value === "personalizado") {
                    // Set specific permissions for "personalizado" profile
                    setPermissions({
                      usuariosSistema: { add: false, edit: true, view: true },
                      obra: { add: true, edit: true, view: true },
                      relatorios: { add: true, edit: true, view: true },
                      gestorTarefas: { add: true, edit: true, view: true },
                      relatoriosGerenciais: { add: true, edit: true, view: true },
                      obraIA: { add: true, edit: true, view: true },
                      clientes: { add: true, edit: true, view: true },
                      fornecedores: { add: true, edit: true, view: true },
                      materiais: { add: true, edit: true, view: true },
                      equipamentos: { add: true, edit: true, view: true },
                      setores: { add: false, edit: false, view: true },
                      horarioTrabalho: { add: true, edit: true, view: true },
                      condicaoClimatica: { add: true, edit: true, view: true },
                      maoObra: { add: true, edit: true, view: true },
                      atividade: { add: true, edit: true, view: true },
                      ocorrencia: { add: true, edit: true, view: true },
                      checklist: { add: true, edit: true, view: true },
                      controleMaterial: { add: true, edit: true, view: true },
                      comentario: { add: true, edit: true, view: true },
                      foto: { add: true, edit: true, view: true },
                      video: { add: true, edit: true, view: true },
                      anexo: { add: true, edit: true, view: true }
                    });
                  }
                }}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o perfil" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="administrador">Administrador</SelectItem>
                    <SelectItem value="cliente">Cliente</SelectItem>
                    <SelectItem value="personalizado">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Status</Label>
                <Select value={userStatus} onValueChange={setUserStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ativo">Ativo</SelectItem>
                    <SelectItem value="inativo">Inativo</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label>Poderá visualizar relatório</Label>
                <Select value={viewReportLevel} onValueChange={setViewReportLevel}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o nível" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    <SelectItem value="preenchendo">Preenchendo relatório</SelectItem>
                    <SelectItem value="aguardando">Aguardando aprovação</SelectItem>
                    <SelectItem value="aprovado">Aprovado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="approveReport"
                checked={approveReport}
                onCheckedChange={setApproveReport}
              />
              <Label htmlFor="approveReport">Aprovar relatório</Label>
            </div>

            {/* Notificações */}
            <div className="grid gap-2">
              <Label>Notificações</Label>
              <div className="flex items-center gap-6 p-4 border rounded-md">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="notificationApp"
                    checked={notifications.app}
                    onCheckedChange={(checked) => 
                      setNotifications(prev => ({ ...prev, app: checked }))
                    }
                  />
                  <Label htmlFor="notificationApp" className="text-sm">APP</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="notificationEmail"
                    checked={notifications.email}
                    onCheckedChange={(checked) => 
                      setNotifications(prev => ({ ...prev, email: checked }))
                    }
                  />
                  <Label htmlFor="notificationEmail" className="text-sm">E-mail</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="notificationWhatsapp"
                    checked={notifications.whatsapp}
                    onCheckedChange={(checked) => 
                      setNotifications(prev => ({ ...prev, whatsapp: checked }))
                    }
                  />
                  <Label htmlFor="notificationWhatsapp" className="text-sm">WhatsApp</Label>
                </div>
              </div>
            </div>

            {/* Tabela de Permissões */}
            <div className="grid gap-2">
              <Label>Permissões</Label>
              <div className="border rounded-md p-4">
                <div className="grid grid-cols-4 gap-4 mb-4 pb-2 border-b">
                  <Label className="font-semibold">Descrição</Label>
                  <Label className="font-semibold text-center">Adicionar</Label>
                  <Label className="font-semibold text-center">Editar</Label>
                  <Label className="font-semibold text-center">Visualizar</Label>
                </div>
                
                {Object.entries({
                  usuariosSistema: "Usuários do sistema",
                  obra: "Obra",
                  relatorios: "Relatórios",
                  gestorTarefas: "Gestor de Tarefas",
                  relatoriosGerenciais: "Relatórios Gerenciais",
                  obraIA: "Obra IA",
                  clientes: "Clientes",
                  fornecedores: "Fornecedores",
                  materiais: "Materiais",
                  equipamentos: "Equipamentos",
                  setores: "Setores/Disciplinas",
                  horarioTrabalho: "Horário de trabalho",
                  condicaoClimatica: "Condição Climática",
                  maoObra: "Mão de Obra",
                  atividade: "Atividade",
                  ocorrencia: "Ocorrência",
                  checklist: "Checklist",
                  controleMaterial: "Controle de Material",
                  comentario: "Comentário",
                  foto: "Foto",
                  video: "Vídeo",
                  anexo: "Anexo"
                }).map(([key, label]) => (
                  <div key={key} className="grid grid-cols-4 gap-4 py-2 items-center">
                    <Label className="text-sm">{label}</Label>
                    <div className="flex justify-center">
                      <Checkbox
                        checked={permissions[key as keyof typeof permissions]?.add || false}
                        onCheckedChange={(checked) => 
                          setPermissions(prev => ({
                            ...prev,
                            [key]: { ...prev[key as keyof typeof permissions], add: checked as boolean }
                          }))
                        }
                      />
                    </div>
                    <div className="flex justify-center">
                      <Checkbox
                        checked={permissions[key as keyof typeof permissions]?.edit || false}
                        onCheckedChange={(checked) => 
                          setPermissions(prev => ({
                            ...prev,
                            [key]: { ...prev[key as keyof typeof permissions], edit: checked as boolean }
                          }))
                        }
                      />
                    </div>
                    <div className="flex justify-center">
                      <Checkbox
                        checked={permissions[key as keyof typeof permissions]?.view || false}
                        onCheckedChange={(checked) => 
                          setPermissions(prev => ({
                            ...prev,
                            [key]: { ...prev[key as keyof typeof permissions], view: checked as boolean }
                          }))
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => handleOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button 
              onClick={() => {
                if (!userName || !userEmail) {
                  toast.error("Nome e e-mail são obrigatórios!");
                  return;
                }
                
                if (onUserCreated) {
                  onUserCreated({
                    name: userName,
                    email: userEmail,
                    role: userRole || "Usuário",
                    phone: userPhone || ""
                  });
                }
                
                // handleOpenChange será chamado automaticamente pelo pai
              }}
            >
              Salvar Usuário
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* User Search Dialog */}
      <Dialog open={isUserSearchOpen} onOpenChange={setIsUserSearchOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[70vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Buscar Usuário</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="userSearch">Buscar por nome ou e-mail</Label>
              <Input
                id="userSearch"
                placeholder="Digite o nome ou e-mail..."
                value={userSearchTerm}
                onChange={(e) => setUserSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="border rounded-md max-h-[400px] overflow-y-auto">
              {filteredClientsAndSuppliers.length > 0 ? (
                <div className="divide-y">
                  {filteredClientsAndSuppliers.map((user) => (
                    <div
                      key={user.id}
                      className="p-4 hover:bg-muted cursor-pointer transition-colors"
                      onClick={() => selectUser(user)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="space-y-1">
                          <div className="font-medium">{user.name}</div>
                          <div className="text-sm text-muted-foreground">{user.email}</div>
                          <div className="text-sm text-muted-foreground">{user.phone}</div>
                        </div>
                        <div className="text-xs bg-secondary px-2 py-1 rounded-md">
                          {user.type === "cliente" ? "Cliente" : "Fornecedor"}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-muted-foreground">
                  {userSearchTerm ? "Nenhum usuário encontrado" : "Digite para buscar usuários"}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsUserSearchOpen(false);
                setUserSearchTerm("");
              }}
            >
              Cancelar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};